import { ArrowLeft, CheckCircle2, Truck } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/src/components/ui/button";
import { useCurrency, useOrder } from "@tagadapay/plugin-sdk/v2";
import { useConfig } from "./hooks/useConfig";

interface ThankYouPageProps {
  orderId: string;
}

export function ThankYouPage({ orderId }: ThankYouPageProps) {
  const { pluginConfig, storeId } = useConfig();

  const { order, isLoading, error, refresh } = useOrder({
    orderId,
    enabled: Boolean(orderId),
  });
  useEffect(() => {
    if (orderId) {
      refresh();
    }
  }, [orderId, refresh]);

  const currentCurrency = useCurrency();

  useEffect(() => {
    if (orderId && !order && !isLoading && !error) {
      refresh();
    }
  }, [orderId, order, isLoading, error, refresh]);

  // Tracking script for TripleWhale and GTM
  useEffect(() => {
    if (!order || isLoading) return;

    const executeTracking = () => {
      // Try to get order from TagadaPayUtils if available, otherwise use component order

      const w = globalThis as any;
      const orderData = w.TagadaPayUtils?.order || order;
      const customer =
        orderData?.customer || orderData?.checkoutSession?.customer;

      if (!orderData || !customer || !orderData.items?.length) {
        console.error("Missing order, customer, or items data.");
        return;
      }

      /* ================================
         TRIPLEWHALE (TriplePixel)
         ================================ */
      if (w.TriplePixel) {
        // Identify customer
        w.TriplePixel("Contact", {
          email: customer.email,
        });
        // Track purchase
        w.TriplePixel("track", "Purchase", {
          orderId: orderData.id,
          total: (orderData.paidAmount || orderData.totalAmount || 0) / 100,
          currency: orderData.currency,
          items: orderData.items.map((item: any) => ({
            id: item.productId || item.orderLineItemProduct?.id,
            name: item.orderLineItemProduct?.name,
            price: (item.amount || item.price || 0) / 100,
            quantity: item.quantity,
          })),
        });
        console.log("[TripleWhale] Purchase event sent");
      }

      /* ================================
         GOOGLE TAG MANAGER (GA4)
         ================================ */
      w.dataLayer = w.dataLayer || [];
      const paidAmount = orderData.paidAmount || orderData.totalAmount || 0;
      const purchaseEvent = {
        event: "purchase",
        ecommerce: {
          value: paidAmount / 100,
          currency: orderData.currency,
          transaction_id: orderData.id,
          items: orderData.items.map((item: any) => ({
            item_id: item.productId || item.orderLineItemProduct?.id,
            item_name: item.orderLineItemProduct?.name,
            price: (item.amount || item.price || 0) / 100,
            quantity: item.quantity,
          })),
        },
        user_data: {
          first_name: customer.firstName || "",
          last_name: customer.lastName || "",
          email: customer.email || "",
          address: orderData.shippingAddress?.address1 || "",
          city: orderData.shippingAddress?.city || "",
          state: orderData.shippingAddress?.state || "",
          postal_code: orderData.shippingAddress?.postal || "",
          country: orderData.shippingAddress?.country || "",
        },
        "gtm.uniqueEventId": Date.now(),
      };
      w.dataLayer.push(purchaseEvent);
      console.log("[GTM] purchase event sent", purchaseEvent);
    };

    // If TagadaPayUtils is available, use ready callback, otherwise execute immediately
    const w = globalThis as any;
    if (w.TagadaPayUtils?.ready) {
      w.TagadaPayUtils.ready(executeTracking);
    } else {
      // Wait a bit for TagadaPayUtils to load, then execute
      const timeout = setTimeout(() => {
        executeTracking();
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [order, isLoading]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f5f3f0] py-8">
        <div className="mx-auto max-w-md">
          <div className="rounded-lg bg-white p-6 shadow">
            <div className="flex items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
              <p className="ml-2 text-gray-600">Loading order details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !order) {
    return (
      <div className="min-h-screen bg-[#f5f3f0] py-8">
        <div className="mx-auto max-w-md">
          <div className="rounded-lg bg-white p-6 shadow">
            <h1 className="mb-4 text-xl font-semibold text-red-600">
              Order Not Found
            </h1>
            <p className="mb-4 text-gray-600">
              {error?.message ||
                "We could not find the order you are looking for."}
            </p>
            <Link to="/checkout">
              <Button className="w-full">Return to Checkout</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Get the current summary using useCurrency hook as priority, then fallback to selectedPresentmentCurrency
  const selectedCurrency =
    currentCurrency?.code ||
    order.checkoutSession?.selectedPresentmentCurrency ||
    order.currency ||
    "USD";
  const currentSummary = order.summaries?.find(
    (summary) => summary.currency === selectedCurrency,
  );

  // Use currency from current summary or fallback
  const currency = currentSummary?.currency || selectedCurrency;

  // Include items from related orders as well
  const relatedOrders = order.relatedOrders || [];
  const allItems = [order, ...relatedOrders].flatMap((o) => o.items || []);
  // Get items for the current currency only (filters out duplicate currency items)
  const currentCurrencyItems = allItems.filter(
    (item) => item.currency === currency,
  );

  console.log(
    `[ThankYouPage] Currency sources - useCurrency: ${currentCurrency?.code}, selectedPresentmentCurrency: ${order.checkoutSession?.selectedPresentmentCurrency}, order.currency: ${order.currency}`,
  );
  console.log(
    `[ThankYouPage] Final selected currency: ${selectedCurrency}, Display currency: ${currency}`,
  );
  console.log(
    `[ThankYouPage] Total items (incl. related): ${allItems.length}, Current currency items: ${currentCurrencyItems.length}`,
  );
  console.log(
    `[ThankYouPage] Available summaries:`,
    order.summaries?.map((s) => s.currency),
  );

  // Calculate totals

  const formatOrderDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getItemImage = (item: any) => {
    const itemName = String(
      item.orderLineItemProduct?.name ||
        item.orderLineItemVariant?.name ||
        item.product?.name ||
        "",
    ).toLowerCase();
    const variantId = item.variantId || item.orderLineItemVariant?.id || "";

    const bundleImageByVariant: Record<string, string> = {
      [pluginConfig.variants.double]:
        "https://img.funnelish.com/41178/571163/1754185730-a2.png",
      [pluginConfig.variants.simple]:
        "https://img.funnelish.com/41178/571163/1754185745-a4.png",
      [pluginConfig.variants.triple]:
        "https://img.funnelish.com/41178/571163/1754185766-a1.png",
      [pluginConfig.variants.quadruple]:
        "https://img.funnelish.com/41178/571163/1754185756-a3.png",
    };

    if (variantId && bundleImageByVariant[variantId]) {
      return bundleImageByVariant[variantId];
    }

    if (itemName.includes("90 day money-back guarantee")) {
      return "//assets.funnelkonnekt.com/c3586120-cca6-11ed-a5ff-e92bf37fd671/1687870531396_nooro90days.png";
    }

    if (variantId === pluginConfig.orderBumpIdExpeditedShippingVariant) {
      return (
        item.orderLineItemVariant?.imageUrl ||
        "//fnsh.imgix.net/16391/132009/1694055488-1688312587252_1_1_.png?auto=compress,format&fit=scale&w=96&h=87"
      );
    }

    if (variantId === pluginConfig.orderBumpIdLifetimeProtectionVariant) {
      return (
        item.orderLineItemVariant?.imageUrl ||
        "//fnsh.imgix.net/16391/132009/1694055494-1688313716977_1.jpg?auto=compress,format&fit=scale&w=96&h=91"
      );
    }

    return item.orderLineItemVariant?.imageUrl || "/placeholder.svg";
  };

  console.log(currentCurrencyItems);

  return (
    <div className="bg-[#f5f3f0] font-sans">
      {/* Header */}
      <header className="bg-brand-green-dark p-2 text-center ">
        <div className="flex items-center justify-center gap-2">
          <CheckCircle2 className="h-6 w-6 text-green-400" />
          <p className="font-bold">ORDER CONFIRMED</p>
        </div>
        <p className="text-sm">
          Thank you for your purchase! Your order has been successfully
          processed.
        </p>
      </header>

      {/* Navigation */}
      <div className="bg-white py-4">
        <div className="container mx-auto flex items-center justify-between px-2 sm:px-4">
          <div />
          <div className="flex items-center gap-2 sm:gap-6">
            <div className="text-right">
              <div className="mb-1 flex items-center justify-end gap-1">
                <CheckCircle2 className="h-3 w-3 text-green-600" />
                <p className="text-xs font-medium text-gray-700 sm:text-sm">
                  Order Confirmed
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="container mx-auto px-2 py-4 sm:px-4 md:px-8 lg:px-12 xl:px-24">
        <div className="grid grid-cols-1 gap-4 sm:gap-6">
          {/* Left Column - Order Details */}
          <div className="space-y-3 sm:space-y-4 lg:col-span-3">
            {/* Success Message */}
            <div className="flex items-center justify-center gap-2 rounded-lg border border-green-300 bg-green-100 p-3 text-center text-sm font-semibold text-green-700 sm:p-4 sm:text-base">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
              Your order has been confirmed and will be processed shortly!
            </div>

            {/* Order Information */}
            <div className="space-y-3 rounded-lg border border-gray-300 bg-white p-3 sm:space-y-4 sm:p-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div>
                  <h2 className="text-lg font-bold sm:text-xl">
                    ORDER DETAILS
                  </h2>
                  <p className="text-sm text-gray-600">Order #{order.id}</p>
                  <p className="text-sm text-gray-600">
                    Placed on {formatOrderDate(order.createdAt)}
                  </p>
                </div>
                <div className="text-right">
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                    {order.status.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Order Items */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold">
                  Items Ordered ({currency})
                </h3>
                {currentCurrencyItems.length > 0 ? (
                  currentCurrencyItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 border-b border-gray-100 pb-3"
                    >
                      <div className="flex-shrink-0">
                        <img
                          src={getItemImage(item)}
                          alt={item.orderLineItemProduct?.name || "Product"}
                          width={60}
                          height={60}
                          className="rounded-lg object-cover"
                        />
                      </div>
                      <div className="flex-grow">
                        <h4 className="font-medium">
                          {item.orderLineItemProduct?.name}
                        </h4>
                        {item.orderLineItemVariant?.name && (
                          <p className="text-sm text-gray-600">
                            {item.orderLineItemVariant.name}
                          </p>
                        )}
                        <p className="text-sm text-gray-500">
                          Quantity: {item.quantity}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="py-4 text-center text-gray-500">
                    <p>No items found for {currency}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Shipping Information */}
            {order.shippingAddress && (
              <div className="space-y-3 rounded-lg border border-gray-300 bg-white p-3 sm:space-y-4 sm:p-4">
                <div className="flex items-center gap-2">
                  <Truck className="h-5 w-5 text-gray-600" />
                  <h2 className="text-lg font-bold sm:text-xl">
                    SHIPPING INFORMATION
                  </h2>
                </div>
                <div className="text-sm">
                  <p className="font-medium">
                    {order.shippingAddress.firstName}{" "}
                    {order.shippingAddress.lastName}
                  </p>
                  <p>{order.shippingAddress.address1}</p>
                  {order.shippingAddress.address2 && (
                    <p>{order.shippingAddress.address2}</p>
                  )}
                  <p>
                    {order.shippingAddress.city}, {order.shippingAddress.state}{" "}
                    {order.shippingAddress.postal}
                  </p>
                  <p>{order.shippingAddress.country}</p>
                  {order.shippingAddress.phone && (
                    <p>Phone: {order.shippingAddress.phone}</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 sm:gap-4">
              <img
                src="https://img.funnelish.com/9938/69802/1678959839-op.webp"
                alt="90 Day Guarantee"
                width={80}
                height={80}
                className="sm:h-[100px] sm:w-[100px]"
              />
              <div>
                <h4 className="text-base font-bold sm:text-lg">
                  90 DAYS GUARANTEE
                </h4>
                <p className="text-pretty text-xs text-gray-600 sm:text-sm">
                  If you are not completely thrilled with your purchase - we are
                  offering you a 90 day guarantee on all purchases. Simply
                  contact our customer support for a full refund or replacement.
                </p>
              </div>
            </div>
            {/* Continue Shopping */}
            <div className="text-center">
              {order.checkoutSession?.returnUrl ? (
                <a
                  href={order.checkoutSession.returnUrl}
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-6 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Continue Shopping
                </a>
              ) : (
                <Link
                  to="/checkout"
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-6 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Return to Checkout
                </Link>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-6 bg-[#eae4da] py-6 sm:mt-8 sm:py-8">
        <div className="container mx-auto px-2 sm:px-4 md:px-8 lg:px-12 xl:px-24">
          <div className="text-center">
            <div className="mb-4 sm:mb-6">
              <img
                src="https://img.funnelish.com/41178/302481/1710818540-Magique%20%281%29.png"
                alt="Magique Logo"
                className="mx-auto h-10 sm:h-12"
                style={{ maxWidth: "240px" }}
              />
            </div>
            <div className="mb-4 flex flex-wrap justify-center gap-4 text-xs sm:mb-6 sm:gap-6 sm:text-sm">
              <a
                href="https://trymagique.com/pages/returns-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#23527c] hover:underline"
              >
                Returns Policy
              </a>
              <a
                href="https://trymagique.com/pages/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#23527c] hover:underline"
              >
                Terms of Service
              </a>
              <a
                href="https://trymagique.com/pages/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#23527c] hover:underline"
              >
                Privacy Policy
              </a>
              <a
                href="https://trymagique.com/pages/shipping-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#23527c] hover:underline"
              >
                Shipping Policy
              </a>
            </div>
            <div className="text-center text-xs leading-relaxed text-gray-700 sm:text-sm">
              <p className="mb-2">
                © 2025 / {pluginConfig.branding.companyName} / All rights
                reserved.
              </p>
              <a
                href="mailto:support@trymagique.com"
                className="text-xs font-semibold sm:text-sm"
              >
                support@trymagique.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
